import { gql, useMutation, useQuery } from '@apollo/client'

import {
  ArtistDigitalMembershipCardQuery,
  ArtistUpdateDigitalMembershipCardMutation,
  ArtistUpdateDigitalMembershipCardMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { Camera } from '@/core/native'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, routerPages, generatePath } = useRouter()
  const { showError, showMessage } = useMessage()
  const { t } = useTranslation('consoleDigitalMembershipDefaultCardDesignEdit')
  const { getImage } = Camera
  const { upload, progress } = useUserTmpUploader(user?.uid || '')

  const { data, loading } = useQuery<ArtistDigitalMembershipCardQuery>(QUERY)

  const [updateDefaultDigitalMembershipCard, { loading: updating }] =
    useMutation<
      ArtistUpdateDigitalMembershipCardMutation,
      ArtistUpdateDigitalMembershipCardMutationVariables
    >(UPDATE_DEFAULT_DIGITAL_MEMBERSHIP_CARD, {
      onCompleted() {
        showMessage(t('editSuccessful'), 'success')
        router.goBack(generatePath(routerPages.ConsoleDigitalMembershipCard))
      },
    })

  const onDrop = async () => {
    const file = await getImage(config.upload.mimeTypes.image)
    if (file.size > config.upload.limit.image) {
      throw new Error('uploadErrorMaximumSize')
    }
    return file
  }

  return {
    state: {
      artistId: user?.uid ?? '',
      progress,
      loading,
      membershipCard: data?.artistDigitalMembershipCard ?? null,
    },
    actions: {
      async onSubmit({ cardDesignPath, textColor, displayAreaColor }) {
        await updateDefaultDigitalMembershipCard({
          variables: {
            inputs: {
              cardDesignPath,
              textColor,
              displayAreaColor,
            },
          },
        })
      },
      async onDrop() {
        const file = await onDrop().catch(error => {
          showError(new Error(t([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(file, false, true)
        const uuid = createUuid()
        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
    },
  }
})

const QUERY = gql`
  query ArtistDigitalMembershipCard {
    artistDigitalMembershipCard {
      cardDesignPath
      displayAreaColor
      textColor
    }
  }
`
const UPDATE_DEFAULT_DIGITAL_MEMBERSHIP_CARD = gql`
  mutation ArtistUpdateDigitalMembershipCard(
    $inputs: ArtistUpdateDigitalMembershipCardInput!
  ) {
    artistUpdateDigitalMembershipCard(inputs: $inputs) {
      artist {
        id
        displayName
        digitalMembershipCard {
          cardDesignPath
          displayAreaColor
          textColor
        }
      }
    }
  }
`
